import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, UserCheck, Pencil } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { vacationCall } from "../../lib/compliance";

export const ReplacementPicks = ({ vacationId, slot, onPick, assign = false, onDone }) => {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const key = slot ? `${slot.date}|${slot.start_time}|${slot.end_time}|${slot.site_id || ""}|${slot.activity ?? ""}` : "";
  useEffect(() => {
    if (!vacationId) return;
    let live = true;
    setList(null); setErr("");
    const params = slot?.date && slot?.start_time && slot?.end_time ? slot : {};
    http.get(`/vacations/${vacationId}/replacements`, { params }).then((r) => live && setList(r.data)).catch((e) => live && setErr(formatApiError(e)));
    return () => { live = false; };
  }, [vacationId, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (a) => {
    if (!assign) { onPick(a); return; }
    setBusy(a.id);
    try { await vacationCall("patch", `/vacations/${vacationId}/assign`, { agent_id: a.id }); toast.success(`${a.name} affecté(e)`); onDone?.(a); }
    catch (e) { if (!e.cancelled) toast.error(e.requested ? e.message : formatApiError(e)); } finally { setBusy(""); }
  };
  if (err) return <span className="text-[11px] text-rose-700" data-testid={`repl-error-${vacationId}`}>Suggestions indisponibles : {err}</span>;
  if (list === null) return <span className="text-[11px] text-slate-500">Recherche de remplaçants…</span>;
  if (!list.length) return <span className="text-[11px] text-slate-500" data-testid={`repl-none-${vacationId}`}>Aucun intervenant connu du site n'est disponible.</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`repl-${vacationId}`}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Remplaçants suggérés</span>
      {list.map((a) => (
        <button key={a.id} type="button" disabled={!!busy} onClick={() => pick(a)} data-testid={`repl-pick-${vacationId}-${a.id}`}
          title={`${a.count} vacation(s) déjà effectuée(s) sur ce site${a.preferred ? " · préconisé" : ""}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100 disabled:opacity-50">
          {a.preferred ? <Star className="h-3 w-3 text-[#C9962E]" /> : <UserCheck className="h-3 w-3" />}{a.name}<span className="text-emerald-600">({a.count})</span>{assign && busy === a.id ? "…" : ""}
        </button>
      ))}
    </div>
  );
};

const N = (x) => String(x).replace(".", ",");

const OpeningForm = ({ agentId, cp, onDone }) => {
  const [f, setF] = useState({ as_of: cp.opening?.as_of || new Date().toISOString().slice(0, 10), n1_remaining: cp.opening?.n1_remaining ?? 0, n_acquired: cp.opening?.n_acquired ?? 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await http.put(`/agents/${agentId}/cp-opening`, { ...f, n1_remaining: Number(f.n1_remaining) || 0, n_acquired: Number(f.n_acquired) || 0 }); toast.success("Solde de départ enregistré"); onDone(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const inp = "w-full border border-slate-300 rounded-md px-2 py-1 text-sm";
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end mt-2" data-testid="cp-opening-form">
      <label className="text-[11px] font-bold text-slate-600">Solde au<input type="date" className={inp} value={f.as_of} onChange={(e) => setF({ ...f, as_of: e.target.value })} data-testid="cp-opening-date" /></label>
      <label className="text-[11px] font-bold text-slate-600">Reliquat N-1 (j)<input type="number" step="0.5" min="0" className={inp} value={f.n1_remaining} onChange={(e) => setF({ ...f, n1_remaining: e.target.value })} data-testid="cp-opening-n1" /></label>
      <label className="text-[11px] font-bold text-slate-600">Acquis en cours (j)<input type="number" step="0.5" min="0" max="30" className={inp} value={f.n_acquired} onChange={(e) => setF({ ...f, n_acquired: e.target.value })} data-testid="cp-opening-acq" /></label>
      <button type="button" disabled={busy || !f.as_of} onClick={save} className="px-3 py-1.5 rounded-md bg-sky-600 text-white text-sm font-bold disabled:opacity-50" data-testid="cp-opening-save">Enregistrer</button>
    </div>
  );
};

export const CpBalanceCard = ({ agentId, can, refreshKey }) => {
  const [cp, setCp] = useState(null);
  const [edit, setEdit] = useState(false);
  const [err, setErr] = useState("");
  const load = () => http.get(`/agents/${agentId}/cp-balance`).then((r) => { setCp(r.data); setErr(""); }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, [agentId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <p className="text-sm text-rose-700" data-testid="cp-balance-error">Compteur de CP indisponible : {err} <button type="button" onClick={load} className="underline">Réessayer</button></p>;
  if (!cp) return null;
  const cells = cp.configured ? [["Reliquat N-1", cp.n1], ["Acquis (depuis le 1er juin)", cp.acquired], ["Pris", cp.taken], ["Posés à venir", cp.planned], ["Demandés", cp.pending]] : [];
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="cp-balance-card">
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex-1">Compteur de congés payés</div>
        {can && <button type="button" onClick={() => setEdit((x) => !x)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid="cp-opening-edit"><Pencil className="h-3 w-3" />Solde de départ</button>}
      </div>
      {cp.configured ? (
        <div className="mt-2 flex flex-wrap items-end gap-4">
          <div><div className="text-3xl font-display font-extrabold text-[#0B2A7A]" data-testid="cp-balance-value">{N(cp.balance)} j</div><div className="text-[11px] text-slate-500">solde disponible</div></div>
          {cells.map(([l, v]) => <div key={l} className="text-sm"><b data-testid={`cp-${l.split(" ")[0].toLowerCase()}`}>{N(v)} j</b><div className="text-[11px] text-slate-500">{l}</div></div>)}
        </div>
      ) : <p className="text-sm text-slate-500 mt-1" data-testid="cp-not-configured">Renseignez la date d'embauche de l'agent ou un solde de départ pour activer le compteur.</p>}
      <p className="text-[11px] text-slate-400 mt-1">2,5 jours ouvrables acquis par mois, période du 1er juin au 31 mai{cp.opening ? ` · solde de départ au ${cp.opening.as_of.split("-").reverse().join("/")}` : ""}.</p>
      {edit && <OpeningForm agentId={agentId} cp={cp} onDone={(d) => { setCp(d); setEdit(false); }} />}
    </div>
  );
};
